import { TaskState } from "gql/graphql";

export interface BubbleConfig {
  buttonClass: string;
  tooltipClass: string;
  tooltipText: string;
  isClickable: boolean;
  isLarge: boolean;
  color: 'yellow' | 'green' | 'purple' | 'red' | 'grey';
}

export interface BubbleConfigParams {
  stepIndex: number;
  currentStep: number;
  totalSteps: number;
  stepState: string;
  taskState?: string;
  canNavigate: boolean;
  disabledDots: boolean;
  responses?: any[];
  stepAlwaysDraft?: number;
  stepAlwaysApproved?: number;
}

type StepType = 'current-in-progress' | 'current-final' | 'previous' | 'future';

/**
 * Determina el tipo de paso según su relación con el paso actual y el estado de la task
 */
function getStepType(
  stepIndex: number,
  currentStep: number,
  totalSteps: number,
  taskState?: string
): StepType {
  const normalizedTaskState = normalizeState(taskState || '');
  const isFinalState = ['review', 'approved', 'error', 'request_error'].includes(normalizedTaskState);
  const isLastStep = stepIndex === totalSteps;
  const isCurrentStep = stepIndex === currentStep;

  // Paso actual Y último paso Y task en estado final
  if (isCurrentStep && isLastStep && isFinalState) {
    return 'current-final';
  }

  if (isCurrentStep) {
    return 'current-in-progress';
  }

  if (stepIndex < currentStep) {
    return 'previous';
  }

  return 'future';
}

function countStepErrors(responses: any[] = []): number {
  return responses.filter(
    (item) => item.state === TaskState.RequestError
  ).length;
}

function normalizeState(state: string): string {
  return state ? state.toLowerCase() : '';
}

/**
 * Determina si un paso anterior debe considerarse "approved" 
 * cuando la task está en estado final (review/approved/error)
 */
function shouldConsiderApproved(
  stepType: StepType,
  stepIndex: number,
  totalSteps: number,
  taskState?: string
): boolean {
  if (stepType !== 'previous') return false;

  const finalStates = ['review', 'approved', 'error', 'request_error'];
  const normalizedTaskState = normalizeState(taskState || '');

  return finalStates.includes(normalizedTaskState) && stepIndex < totalSteps;
}

export function getBubbleConfig(params: BubbleConfigParams): BubbleConfig {
  const {
    stepIndex,
    currentStep,
    totalSteps,
    stepState,
    taskState,
    canNavigate,
    disabledDots,
    responses = [],
    stepAlwaysDraft,
    stepAlwaysApproved
  } = params;

  const stepType = getStepType(stepIndex, currentStep, totalSteps, taskState);
  const normalizedState = normalizeState(stepState);
  const normalizedTaskState = normalizeState(taskState || '');

  const isForcedDraft = stepAlwaysDraft === stepIndex;
  const isForcedApproved = stepAlwaysApproved === stepIndex;

  const effectiveState = isForcedDraft
    ? 'draft'
    : isForcedApproved
      ? 'approved'
      : normalizedState;

  // Caso especial: paso actual ya completado (task en estado final, pero no es el último paso)
  const isCurrentStepCompleted =
    stepIndex === currentStep &&
    stepIndex < totalSteps &&
    ['review', 'approved', 'error', 'request_error'].includes(normalizedTaskState);

  if (isCurrentStepCompleted && !isForcedDraft) {
    return {
      buttonClass: 'dots-button dots-button-approved-active',
      tooltipClass: 'tooltip-button tooltip-button-approved',
      tooltipText: `STEP ${stepIndex} | APPROVED`,
      isClickable: false,
      isLarge: true,
      color: 'green'
    };
  }

  // Paso actual en progreso
  if (stepType === 'current-in-progress') {
    return getCurrentInProgressConfig(disabledDots, effectiveState, responses, stepIndex);
  }

  // Paso actual final (último paso en review/error/approved)
  if (stepType === 'current-final') {
    return getCurrentFinalConfig(stepIndex, normalizedTaskState, responses, isForcedDraft);
  }

  // Pasos anteriores (completados)
  const isExceptionApproved = shouldConsiderApproved(stepType, stepIndex, totalSteps, taskState);

  if (stepType === 'previous' || isExceptionApproved || isForcedApproved) {
    return getPreviousStepConfig(stepIndex, canNavigate, disabledDots);
  }

  // Pasos futuros
  return getFutureStepConfig(
    stepIndex,
    responses,
    disabledDots,
    totalSteps,
    normalizedTaskState,
    isForcedDraft,
    canNavigate
  );
}

/**
 * Paso actual en progreso: siempre amarillo (task no está en estado final)
 * Excepción: tasks sin navegación pueden mostrar review/error del paso individual
 */
function getCurrentInProgressConfig(
  disabledDots: boolean,
  state: string,
  responses: any[],
  stepIndex: number
): BubbleConfig {
  if (disabledDots) {
    if (state === 'review') {
      return {
        buttonClass: 'dots-button dots-button-review-active',
        tooltipClass: 'tooltip-button tooltip-button-review',
        tooltipText: `STEP ${stepIndex} | IN REVIEW`,
        isClickable: false,
        isLarge: true,
        color: 'purple'
      };
    }

    if (state === 'error' || state === 'request_error') {
      const errorCount = countStepErrors(responses);
      const errorText = errorCount > 1 ? `${errorCount} ERRORS` : errorCount === 1 ? '1 ERROR' : 'ERROR';

      return {
        buttonClass: 'dots-button dots-button-error-active',
        tooltipClass: 'tooltip-button tooltip-button-error',
        tooltipText: `STEP ${stepIndex} | ${errorText}`,
        isClickable: false,
        isLarge: true,
        color: 'red'
      };
    }
  }

  // Paso actual en progreso: siempre amarillo, sin hover text
  return {
    buttonClass: 'dots-button dots-button-active',
    tooltipClass: 'tooltip-button tooltip-button-draft',
    tooltipText: '',
    isClickable: false,
    isLarge: true,
    color: 'yellow'
  };
}

/**
 * Paso actual final: último paso cuando la task está en review/error/approved
 */
function getCurrentFinalConfig(
  stepIndex: number,
  taskState: string,
  responses: any[],
  isForcedDraft: boolean
): BubbleConfig {
  const baseConfig = {
    isClickable: false,
    isLarge: true
  };

  if (taskState === 'review') {
    return {
      ...baseConfig,
      buttonClass: 'dots-button dots-button-review-active',
      tooltipClass: 'tooltip-button tooltip-button-review',
      tooltipText: isForcedDraft ? `STEP ${stepIndex}` : `STEP ${stepIndex} | IN REVIEW`,
      color: 'purple'
    };
  }

  if (taskState === 'error' || taskState === 'request_error') {
    const errorCount = countStepErrors(responses);
    const errorText = errorCount > 1 ? `${errorCount} ERRORS` : errorCount === 1 ? '1 ERROR' : 'ERROR';

    return {
      ...baseConfig,
      buttonClass: 'dots-button dots-button-error-active',
      tooltipClass: 'tooltip-button tooltip-button-error',
      tooltipText: isForcedDraft ? `STEP ${stepIndex}` : `STEP ${stepIndex} | ${errorText}`,
      color: 'red'
    };
  }

  if (taskState === 'approved') {
    return {
      ...baseConfig,
      buttonClass: 'dots-button dots-button-approved-active',
      tooltipClass: 'tooltip-button tooltip-button-approved',
      tooltipText: isForcedDraft ? `STEP ${stepIndex}` : `STEP ${stepIndex} | APPROVED`,
      color: 'green'
    };
  }

  return {
    ...baseConfig,
    buttonClass: 'dots-button dots-button-active',
    tooltipClass: 'tooltip-button tooltip-button-draft',
    tooltipText: `STEP ${stepIndex}`,
    color: 'yellow'
  };
}

/**
 * Pasos anteriores: verdes, con hover si son navegables
 */
function getPreviousStepConfig(
  stepIndex: number,
  canNavigate: boolean,
  disabledDots: boolean,
): BubbleConfig {
  const tooltipText = `STEP ${stepIndex} | APPROVED`;

  if (disabledDots) {
    return {
      buttonClass: 'dots-button dots-button-success',
      tooltipClass: 'tooltip-button tooltip-button-approved',
      tooltipText,
      isClickable: false,
      isLarge: true,
      color: 'green'
    };
  }

  const baseClass = 'dots-button dots-button-success';
  const hoverClass = canNavigate ? 'dots-button-prev-hover' : '';

  return {
    buttonClass: `${baseClass} ${hoverClass}`.trim(),
    tooltipClass: 'tooltip-button tooltip-button-approved',
    tooltipText,
    isClickable: canNavigate,
    isLarge: false,
    color: 'green'
  };
}

/**
 * Pasos futuros: según estado de la task si es el último paso en estado final
 */
function getFutureStepConfig(
  stepIndex: number,
  responses: any[],
  disabledDots: boolean,
  totalSteps: number,
  taskState: string,
  isForcedDraft: boolean,
  canNavigate: boolean
): BubbleConfig {
  const isLastStep = stepIndex === totalSteps;
  const isFinalState = ['review', 'approved', 'error', 'request_error'].includes(taskState);

  // Último paso con estado final (review/error/approved)
  if (isLastStep && isFinalState) {
    return getLastStepFinalConfig(
      stepIndex,
      taskState,
      responses,
      disabledDots,
      canNavigate,
      isForcedDraft
    );
  }

  if (isFinalState && !isLastStep) {
    return {
      buttonClass: 'dots-button dots-button-success dots-button-prev-hover',
      tooltipClass: 'tooltip-button tooltip-button-approved',
      tooltipText: `STEP ${stepIndex} | APPROVED`,
      isClickable: canNavigate,
      isLarge: disabledDots,
      color: 'green'
    }
  }

  // Paso futuro sin estado especial
  return {
    buttonClass: `dots-button ${canNavigate ? 'dots-button-prev-hover' : 'disabled'}`,
    tooltipClass: 'tooltip-button',
    tooltipText: '',
    isClickable: canNavigate,
    isLarge: disabledDots,
    color: 'grey'
  };
}

/**
 * Último paso futuro con estado final: color y comportamiento según el estado
 */
function getLastStepFinalConfig(
  stepIndex: number,
  taskState: string,
  responses: any[],
  disabledDots: boolean,
  canNavigate: boolean,
  isForcedDraft: boolean
): BubbleConfig {
  const tooltipTextPrefix = `STEP ${stepIndex}`;

  if (taskState === 'review') {
    const baseClass = disabledDots ? 'dots-button dots-button-review-active' : 'dots-button dots-button-success-review';
    const hoverClass = canNavigate && !disabledDots ? 'dots-button-prev-hover' : '';

    return {
      buttonClass: `${baseClass} ${hoverClass}`.trim(),
      tooltipClass: 'tooltip-button tooltip-button-review',
      tooltipText: isForcedDraft ? tooltipTextPrefix : `${tooltipTextPrefix} | IN REVIEW`,
      isClickable: canNavigate,
      isLarge: disabledDots,
      color: 'purple'
    };
  }

  if (taskState === 'error' || taskState === 'request_error') {
    const errorCount = countStepErrors(responses);
    const errorText = errorCount > 1 ? `${errorCount} ERRORS` : errorCount === 1 ? '1 ERROR' : 'ERROR';
    const baseClass = disabledDots ? 'dots-button dots-button-error-active' : 'dots-button dots-button-success-error';
    const hoverClass = canNavigate && !disabledDots ? 'dots-button-prev-hover' : '';

    return {
      buttonClass: `${baseClass} ${hoverClass}`.trim(),
      tooltipClass: 'tooltip-button tooltip-button-error',
      tooltipText: isForcedDraft ? tooltipTextPrefix : `${tooltipTextPrefix} | ${errorText}`,
      isClickable: canNavigate,
      isLarge: disabledDots,
      color: 'red'
    };
  }

  if (taskState === 'approved') {
    const baseClass = disabledDots ? 'dots-button dots-button-approved-active' : 'dots-button dots-button-success';
    const hoverClass = canNavigate && !disabledDots ? 'dots-button-prev-hover' : '';

    return {
      buttonClass: `${baseClass} ${hoverClass}`.trim(),
      tooltipClass: 'tooltip-button tooltip-button-approved',
      tooltipText: isForcedDraft ? tooltipTextPrefix : `${tooltipTextPrefix} | APPROVED`,
      isClickable: canNavigate,
      isLarge: disabledDots,
      color: 'green'
    };
  }

  return {
    buttonClass: 'dots-button',
    tooltipClass: 'tooltip-button',
    tooltipText: '',
    isClickable: false,
    isLarge: disabledDots,
    color: 'grey'
  };
}

/**
 * Determina si un paso está deshabilitado para navegación
 */
export function isStepDisabled(
  targetStep: number,
  currentStep: number,
  disabledDots: boolean,
  canNavigateToStep?: (step: number) => boolean
): boolean {
  if (disabledDots) return true;

  if (canNavigateToStep) {
    return !canNavigateToStep(targetStep);
  }

  return currentStep <= targetStep;
}